// Excel export of a past month's portfolio: every job and its financials as of that month's
// end (#613). Built from the same MonthPortfolio the page shows, so the file and the screen
// cannot differ.
//
// Numbers go out as NUMBERS, not preformatted strings, because the first thing finance does
// with this file is add a column of their own (the same rule as the Job Cost report's export).
import * as XLSX from 'xlsx';
import type { AccrualKind, PortfolioAccruals, PortfolioAsOf, PortfolioAsOfJob } from './apiClient';
import { closedFinalityNote, type MonthJob, type MonthPortfolio } from './portfolioMonth';
import { formatPeriod } from './wipHistory';
import { UNIT_LABEL } from './accruals';
import { fmtDate, fmtDateTime, todayInputValue } from './dates';

const cents = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const MONEY = '#,##0.00;(#,##0.00)';
const PCT = '0.0%';
const PTS = '0.0';

const HEALTH_WORDS: Record<string, string> = { green: 'On plan', orange: 'Margin fading', red: 'Fading & underbilled' };

/** What the Figures column says: whether the number is the closed one or a re-cut. */
export function figuresLabel(monthClosed: boolean, job: MonthJob): string {
  if (job.final) return 'Final (closed)';
  return monthClosed ? 'Re-cut: nothing was stored for this job at close' : 'Re-cut (month not closed)';
}

type Cell = string | number | null;

/** The job's name, with a note when the project has been deleted since the month: it is still in
 *  the month (the month is what was frozen), and the person reading should know why. */
const nameOf = (j: MonthJob) => (j.deleted ? `${j.figures.title} (deleted since)` : j.figures.title);

/** Number formats by column, applied after the sheet is built. */
function applyFormats(ws: XLSX.WorkSheet, firstDataRow: number, lastDataRow: number, formats: Record<number, string>) {
  for (let r = firstDataRow; r <= lastDataRow; r++) {
    for (const [c, z] of Object.entries(formats)) {
      const cell = ws[XLSX.utils.encode_cell({ r, c: Number(c) })];
      if (cell && cell.t === 'n') cell.z = z;
    }
  }
}

const ALL_JOBS_HEAD = [
  'Job', 'Name', 'Division',
  'Contract', 'Billed', 'Estimate (budget)', 'Cost to date', 'Projected final cost',
  '% complete', 'Earned', 'Over/(under) billed',
  'Projected profit', 'Projected margin', 'Estimate profit', 'Estimate margin', 'Fade ($)', 'Fade (pts)',
  'Indirects (GL 59998)', 'Super profit', 'Super margin', 'Left to bill',
  'Health', 'Figures', 'Freeze taken',
];
// Column index -> number format, for the columns above.
const ALL_JOBS_FORMATS: Record<number, string> = {
  3: MONEY, 4: MONEY, 5: MONEY, 6: MONEY, 7: MONEY, 8: PCT, 9: MONEY, 10: MONEY,
  11: MONEY, 12: PCT, 13: MONEY, 14: PCT, 15: MONEY, 16: PTS, 17: MONEY, 18: MONEY, 19: PCT, 20: MONEY,
};

function jobRow(j: MonthJob, monthClosed: boolean): Cell[] {
  const f = j.figures;
  // A job with no synced estimate has a MISSING budget: its estimate, fade and health are blank,
  // never a zero or a fade against a phantom $0 (the job's own tab hides them the same way).
  const est = !j.noEstimate;
  return [
    f.jobNumber, nameOf(j), f.division,
    cents(f.revised), cents(f.billed), est ? cents(f.budget) : null, cents(f.actual), cents(f.eac),
    f.pctComplete, cents(f.earned), cents(f.overUnderBilled),
    cents(f.projectedProfit), f.projectedMarginPct, est ? cents(f.estimateProfit) : null, est ? f.estimateMarginPct : null,
    est ? cents(f.fadeDollars) : null, est ? Math.round(f.fadePts * 1000) / 10 : null,
    cents(f.indirectActual), cents(f.superProfit), f.superMarginPct, cents(f.leftToBill),
    est ? (HEALTH_WORDS[f.health] ?? f.health) : 'No estimate synced', figuresLabel(monthClosed, j), fmtDateTime(j.frozenAt),
  ];
}

/**
 * The company line: sums of the money columns, ratios recomputed from the sums (never an
 * average of the jobs' ratios).
 *
 * ⚠️ Each money total adds the jobs' ROUNDED cells, the ones the sheet shows, not the unrounded
 * figures behind them. Finance adds the column to tie out; rounding the sum of unrounded
 * values would leave a cent or three between this row and =SUM() of what is above it.
 */
function totalRow(jobs: MonthJob[]): Cell[] {
  const sum = (pick: (j: MonthJob) => number) => cents(jobs.reduce((a, j) => a + cents(pick(j)), 0));
  // The ratios come from the unrounded sums.
  const raw = (pick: (j: MonthJob) => number) => jobs.reduce((a, j) => a + pick(j), 0);
  const revised = raw((j) => j.figures.revised);
  const actual = raw((j) => j.figures.actual);
  const eac = raw((j) => j.figures.eac);
  const projectedProfit = raw((j) => j.figures.projectedProfit);
  const superProfit = raw((j) => j.figures.superProfit);
  const margin = revised ? projectedProfit / revised : 0;

  // The estimate columns total over the jobs that HAVE an estimate only, because that is all that
  // is in them: a job with none is blank there, and counting it would leave a total that does not
  // tie to its column. Fade points compare the margin and the estimate margin of those same jobs.
  const withEst = jobs.filter((j) => !j.noEstimate);
  const sumE = (pick: (j: MonthJob) => number) => cents(withEst.reduce((a, j) => a + cents(pick(j)), 0));
  const estRevised = withEst.reduce((a, j) => a + j.figures.revised, 0);
  const estProjected = withEst.reduce((a, j) => a + j.figures.projectedProfit, 0);
  const estEstimateProfit = withEst.reduce((a, j) => a + j.figures.estimateProfit, 0);
  const estMarginOfEstJobs = estRevised ? estEstimateProfit / estRevised : 0;
  const marginOfEstJobs = estRevised ? estProjected / estRevised : 0;
  return [
    'Company total', `${jobs.length} job${jobs.length === 1 ? '' : 's'}`, '',
    sum((j) => j.figures.revised), sum((j) => j.figures.billed), sumE((j) => j.figures.budget), sum((j) => j.figures.actual), sum((j) => j.figures.eac),
    eac ? actual / eac : 0, sum((j) => j.figures.earned), sum((j) => j.figures.overUnderBilled),
    sum((j) => j.figures.projectedProfit), margin, sumE((j) => j.figures.estimateProfit), estMarginOfEstJobs,
    sumE((j) => j.figures.fadeDollars), Math.round((marginOfEstJobs - estMarginOfEstJobs) * 1000) / 10,
    sum((j) => j.figures.indirectActual), sum((j) => j.figures.superProfit), revised ? superProfit / revised : 0, sum((j) => j.figures.leftToBill),
    '', '', '',
  ];
}

/** The header block that travels WITH the file: which month, closed or not, and what the
 *  figures are made of. A workbook that leaves the app without saying so is the same trap as
 *  an on-screen number with no note. */
function headerBlock(data: PortfolioAsOf, jobs: MonthJob[]): Cell[][] {
  const n = jobs.length;
  const recut = jobs.filter((j) => !j.final).length;
  const status = data.closed
    ? `Closed${data.closed.by ? ` by ${data.closed.by}` : ''} on ${fmtDateTime(data.closed.at)}. ${closedFinalityNote(n, recut)}${recut > 0 ? ' The Figures column says which.' : ''}`
    : `NOT closed. Cost is every GL entry dated through ${fmtDate(data.through)} as Intacct has it now, so it can still change until accounting closes the month.`;
  const block: Cell[][] = [
    [`Work-in-Progress: ${formatPeriod(data.period)} month end`],
    [],
    ['Status', status],
    ['Cost counted through', fmtDate(data.through)],
    ['Contract, estimate and purchase orders', 'As the month-end freeze recorded them (see "Freeze taken"); a change entered after month end is not in this month.'],
    ['Forecast and budget transfers', 'As the PM had them at the freeze.'],
    ['Exported', fmtDateTime(new Date().toISOString())],
    ['Jobs in this sheet', n],
  ];
  const gone = jobs.filter((j) => j.deleted).length;
  if (gone > 0) {
    block.push(['Note', `${gone} job${gone === 1 ? ' has' : 's have'} been deleted from the Project Tracker since this month and ${gone === 1 ? 'is' : 'are'} still counted in it, marked "(deleted since)" in the name: the month is what was frozen, so a deletion does not change it.`]);
  }
  const noEst = jobs.filter((j) => j.noEstimate).length;
  if (noEst > 0) {
    block.push(['⚠', `${noEst} job${noEst === 1 ? ' had' : 's had'} no estimate synced at month end. Its estimate, estimate profit, estimate margin, fade and health cells (and the budget and variance on the By cost code sheet) are blank, not zero, and ${noEst === 1 ? 'it is' : 'they are'} left out of those totals. Contract, cost and billing are unaffected.`]);
  }
  if (!data.provenance.actualEnabled) block.push(['⚠', 'The Intacct GL cost sync is switched off, so Cost to date is illustrative.']);
  if (!data.provenance.budgetEnabled) block.push(['⚠', 'The Intacct estimate sync is switched off, so the estimate is missing, not zero.']);
  return block;
}

// ── The Accruals sheet ──────────────────────────────────────────────────────
// The PMs' month-end accrual schedule, every job, one line per accrual: what accounting books as the
// month's journal (month-end review, 2026-10-29). The journal columns come first (job, vendor,
// description, cost code, kind, amount) so the table pastes as it stands; the line's own inputs sit to
// the right, so the amount can be checked against them.
const ACCRUAL_KIND_LABEL: Record<AccrualKind, string> = { material: 'Material', equipment: 'Equipment', subcontract: 'Subcontract' };
const ACCRUALS_HEAD = [
  'Job', 'Name', 'Vendor', 'Description', 'Cost code', 'Kind', 'Amount',
  'Carry-over', 'Newly accrued', 'Cost per unit', 'Units', 'Per', 'Contract value', '% complete', '% billed',
  'Saved by', 'Saved at',
];
const PCT_PTS = '0.0##%';
const ACCRUALS_FORMATS: Record<number, string> = { 6: MONEY, 7: MONEY, 8: MONEY, 9: MONEY, 10: '#,##0.00', 12: MONEY, 13: PCT_PTS, 14: PCT_PTS };

/** Percent POINTS as stored (65.5) → the fraction Excel's % format wants (0.655), without float noise. */
const pointsToFraction = (p: number | null): number | null => (p == null ? null : Math.round(p * 1000) / 100000);

function accrualsSheet(accruals: PortfolioAccruals): XLSX.WorkSheet {
  const { lines, totals, deletedTotals } = accruals;
  // Jobs no longer in the Tracker are listed apart, under their own heading and total, so the table the
  // journal is booked from only ever holds jobs that exist and its Total can be taken as it stands.
  const live = lines.filter((l) => !l.deleted);
  const gone = lines.filter((l) => l.deleted);
  const block: Cell[][] = [
    [`Accruals: ${formatPeriod(accruals.period)}`],
    [],
    ['What this is', 'The PMs’ month-end accrual schedule: cost incurred in the month that Intacct has not recognized yet (material bought, equipment rented, subcontract work in place). One line per accrual.'],
    ['Not in the other sheets', 'A schedule only. These amounts are NOT in Cost to date, the projection or any figure on the other sheets, so booking them as a journal does not count them twice.'],
    ['As saved', 'As the PMs have them saved when this file was made. Closing the month does not freeze accruals, so a PM can still change them afterward.'],
    ['Exported', fmtDateTime(new Date().toISOString())],
  ];
  if (!accruals.ready) {
    block.push(['⚠', 'Accruals are not set up on this database yet, so there is nothing to list. This is not the same as no accruals being entered.']);
  } else if (lines.length === 0) {
    block.push(['Result', 'No accruals are saved for this month.']);
  } else {
    block.push(
      ['Jobs with accruals', accruals.jobs],
      ['Lines', live.length],
      ['Material', cents(totals.material)],
      ['Equipment', cents(totals.equipment)],
      ['Subcontract', cents(totals.subcontract)],
      ['Total accruals', cents(totals.total)],
    );
    if (gone.length) {
      block.push(
        ['Deleted jobs', `${accruals.deletedJobs} (${gone.length} ${gone.length === 1 ? 'line' : 'lines'}), listed apart below the table`],
        ['Their accruals', cents(deletedTotals.total)],
        ['', 'The deleted jobs’ accruals are NOT in Total accruals above, nor in the Total under the table.'],
      );
    }
  }
  const accrualRow = (l: PortfolioAccruals['lines'][number]): Cell[] => [
    l.jobNumber ?? '(job not found)',
    l.title ? (l.deleted ? `${l.title} (deleted since)` : l.title) : '',
    l.vendor ?? '', l.description ?? '', l.costCode ?? '',
    ACCRUAL_KIND_LABEL[l.kind] ?? l.kind,
    cents(l.total),
    l.carryOver, l.newAccrual, l.unitCost, l.units, l.unit ? UNIT_LABEL[l.unit] : '', l.contractValue,
    pointsToFraction(l.pctComplete), pointsToFraction(l.pctBilled),
    l.savedBy ?? '', fmtDateTime(l.savedAt),
  ];
  const totalRow = (label: string, amount: number): Cell[] => [label, '', '', '', '', '', cents(amount), '', '', '', '', '', '', null, null, '', ''];

  const aoa: Cell[][] = [...block, []];
  const spans: Array<[number, number]> = [];      // the rows that take the number formats
  const addTable = (list: PortfolioAccruals['lines'], label: string, amount: number) => {
    const first = aoa.length;
    aoa.push(...list.map(accrualRow), ...(list.length ? [totalRow(label, amount)] : []));
    spans.push([first, aoa.length - 1]);
  };
  // The header stays even when there is nothing to list (the "no accruals" and "not set up" cases),
  // but not as an empty table ahead of a deleted-jobs list.
  if (live.length || !gone.length) aoa.push(ACCRUALS_HEAD);
  addTable(live, 'Total', totals.total);
  if (gone.length) {
    aoa.push([], ['Deleted jobs: NOT in the total above'], ACCRUALS_HEAD);
    addTable(gone, 'Total (deleted jobs)', deletedTotals.total);
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  for (const [first, last] of spans) applyFormats(ws, first, last, ACCRUALS_FORMATS);
  ws['!cols'] = [
    { wch: 11 }, { wch: 30 }, { wch: 26 }, { wch: 34 }, { wch: 12 }, { wch: 13 }, { wch: 15 },
    { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 8 }, { wch: 15 }, { wch: 11 }, { wch: 11 },
    { wch: 20 }, { wch: 20 },
  ];
  return ws;
}

export interface MonthWorkbook { wb: XLSX.WorkBook; fileName: string }

/**
 * `accruals` adds the Accruals sheet. It is a separate argument, not part of `PortfolioAsOf`, because the
 * month view does not need it: only the export reads it, and fetches it when the button is pressed.
 */
export function buildMonthWorkbook(data: PortfolioAsOf, portfolio: MonthPortfolio, accruals?: PortfolioAccruals): MonthWorkbook {
  const monthClosed = !!data.closed;
  const jobs = [...portfolio.jobs].sort((a, b) =>
    a.figures.division.localeCompare(b.figures.division) || a.figures.jobNumber.localeCompare(b.figures.jobNumber));

  // ── Sheet 1: one row per job ─────────────────────────────────────────────
  const head = headerBlock(data, jobs);
  const aoa: Cell[][] = [...head, [], ALL_JOBS_HEAD, ...jobs.map((j) => jobRow(j, monthClosed))];
  if (jobs.length) aoa.push(totalRow(jobs));
  const ws1 = XLSX.utils.aoa_to_sheet(aoa);
  const firstData = head.length + 2;
  applyFormats(ws1, firstData, firstData + jobs.length, ALL_JOBS_FORMATS);
  ws1['!cols'] = [{ wch: 11 }, { wch: 32 }, { wch: 16 }, ...Array.from({ length: ALL_JOBS_HEAD.length - 3 }, () => ({ wch: 16 })), ];
  ws1['!cols']![22] = { wch: 44 };
  ws1['!cols']![23] = { wch: 22 };

  // ── Sheet 2: cost-code detail ────────────────────────────────────────────
  const codeHead = ['Job', 'Name', 'Cost code', 'Description', 'Group', 'Budget (after transfers)', 'Committed', 'Cost to date', 'Cost to complete', 'Projected final cost', 'Over/(under) budget'];
  const codeRows: Cell[][] = [];
  for (const j of jobs) {
    for (const r of j.rows) {
      // Same rule as the all-jobs sheet: a job with no synced estimate has a MISSING budget, so the
      // budget and the variance against it are blank here too, not a $0 budget with an overrun.
      // Cost, commitments and the projection (cost to date plus cost to complete) are real.
      codeRows.push([
        j.figures.jobNumber, nameOf(j), r.costCode ?? '(no cost code)', r.costCodeName ?? '', r.costTypeGroup,
        j.noEstimate ? null : cents(r.budget), cents(r.committed), cents(r.actual), cents(r.ctc), cents(r.eac),
        j.noEstimate ? null : cents(r.overUnder),
      ]);
    }
  }
  const ws2 = XLSX.utils.aoa_to_sheet([codeHead, ...codeRows]);
  applyFormats(ws2, 1, codeRows.length, { 5: MONEY, 6: MONEY, 7: MONEY, 8: MONEY, 9: MONEY, 10: MONEY });
  ws2['!cols'] = [{ wch: 11 }, { wch: 32 }, { wch: 12 }, { wch: 34 }, { wch: 14 }, ...Array.from({ length: 6 }, () => ({ wch: 16 }))];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws1, 'All jobs');
  XLSX.utils.book_append_sheet(wb, ws2, 'By cost code');
  if (accruals) XLSX.utils.book_append_sheet(wb, accrualsSheet(accruals), 'Accruals');

  // ── Sheet 3, only when something was left out: say so, never drop a job silently ──
  const left: Cell[][] = [
    ...data.skipped.map((s): Cell[] => [s.jobNumber, '', s.reason]),
    ...portfolio.noContract.map((j: PortfolioAsOfJob): Cell[] => [j.jobNumber, j.title, 'No contract value at month end (the live portfolio leaves these out too).']),
  ];
  if (left.length) {
    const ws3 = XLSX.utils.aoa_to_sheet([['Job', 'Name', 'Why it is not in the other sheets'], ...left]);
    ws3['!cols'] = [{ wch: 11 }, { wch: 32 }, { wch: 80 }];
    XLSX.utils.book_append_sheet(wb, ws3, 'Not included');
  }

  // ⚠️ The EXPORTER'S calendar date, not the UTC one (see exportWorkbook in JobCostReport).
  return { wb, fileName: `wip-portfolio-${data.period}-month-end-exported-${todayInputValue()}.xlsx` };
}

export function downloadMonthWorkbook(data: PortfolioAsOf, portfolio: MonthPortfolio, accruals?: PortfolioAccruals) {
  const { wb, fileName } = buildMonthWorkbook(data, portfolio, accruals);
  XLSX.writeFile(wb, fileName);
}
